import {
  useFeatureFlags,
  useLoggedInUser,
  useProductAssetsByUser,
} from '@tcc/shared-data-lib';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useFeatureFlagsConfigs,
  useProductAssetsByUserConfigs,
  useUserLoggedInConfigs,
} from '../../utils/sharedData';
import { useImpersonationMode } from '../useImpersonationMode';
import {
  byAllowedContryCodes,
  byAllowedFeatureAssets,
  byAllowedImpesonationModes,
  byAllowedRoles,
  byExcludedByFeaureFlag,
  byExcludedContryCodes,
  byExcludededRoles,
  byExcludedImpersonationModes,
  byRequiredFeatureFlags,
} from './filters';
import {
  byOrderOrDefault,
  descriptorToNavigationLink,
  toExperimentalSpaLinks,
  toNavigationItem,
} from './mappers';
import { LinkDescriptor, NavigationItem } from './types';
import { useLinkDescriptors } from './useLinkDescriptors';

interface NavigationItemsResult {
  isLoading: boolean;
  navigationItems: NavigationItem[];
}

export const useNavigationItems = (): NavigationItemsResult => {
  const location = useLocation();
  const { user } = useLoggedInUser(useUserLoggedInConfigs);
  const { data: assets, isLoading: isLoadingAssets } = useProductAssetsByUser(
    useProductAssetsByUserConfigs(user),
  );
  const { featureFlags, isLoading: isLoadingFeatureFlags } = useFeatureFlags(
    useFeatureFlagsConfigs(user),
  );
  const { impersonationModes, isLoading: isLoadingModes } =
    useImpersonationMode();
  const { linkDescriptors, menuDescriptors, deferredDescriptors } =
    useLinkDescriptors();

  const accessibleDeferredDescriptors = useMemo(
    () => deferredDescriptors.filter(({ canAccess }) => canAccess),
    [deferredDescriptors],
  );

  const isLoading =
    !user || isLoadingAssets || isLoadingFeatureFlags || isLoadingModes;

  const filterLinks = useCallback(
    (links: LinkDescriptor[]) => {
      const userRoles = user?.details.roles || [];
      const userCountryCode = user?.details.countryCode || '';
      const safeFeatureFlags = featureFlags || {};

      return links
        .filter((descriptor) => byAllowedRoles(userRoles, descriptor))
        .filter((descriptor) => byExcludededRoles(userRoles, descriptor))
        .filter((descriptor) =>
          byAllowedContryCodes(userCountryCode, descriptor),
        )
        .filter((descriptor) =>
          byExcludedContryCodes(userCountryCode, descriptor),
        )
        .filter((descriptor) =>
          byAllowedFeatureAssets(
            assets?.userSubscription?.featuresByUser || [],
            descriptor,
          ),
        )
        .filter((descriptor) =>
          byRequiredFeatureFlags(safeFeatureFlags, descriptor),
        )
        .filter((descriptor) =>
          byExcludedByFeaureFlag(safeFeatureFlags, descriptor),
        )
        .filter((descriptor) =>
          byAllowedImpesonationModes(impersonationModes, descriptor),
        )
        .filter((descriptor) =>
          byExcludedImpersonationModes(impersonationModes, descriptor),
        );
    },
    [assets, featureFlags, impersonationModes, user],
  );

  const links = useMemo(() => {
    if (isLoading) return [];
    return filterLinks(linkDescriptors)
      .map((descriptor) => descriptorToNavigationLink(descriptor, location))
      .map(toExperimentalSpaLinks)
      .map(toNavigationItem);
  }, [isLoading, linkDescriptors, filterLinks, location]);

  const menues = useMemo(() => {
    if (isLoading) return [];
    return menuDescriptors
      .map((menuDescriptor) => {
        const links = filterLinks(menuDescriptor.links);

        return {
          ...menuDescriptor,
          links: links
            .map((descriptor) =>
              descriptorToNavigationLink(descriptor, location),
            )
            .map(toExperimentalSpaLinks),
        };
      })
      .filter(
        (menuDescriptor) =>
          // All links in this menu were filtered out, do not show it.
          menuDescriptor.links.length,
      )
      .map(toNavigationItem);
  }, [isLoading, menuDescriptors, filterLinks, location]);

  const deferredItems = useMemo(() => {
    if (isLoading) return [];
    return filterLinks(accessibleDeferredDescriptors)
      .map((descriptor) => descriptorToNavigationLink(descriptor, location))
      .map(toExperimentalSpaLinks)
      .map(toNavigationItem);
  }, [
    isLoading,
    accessibleDeferredDescriptors,
    filterLinks,
    location,
  ]);

  const navigationItems = useMemo(
    () => [...links, ...menues, ...deferredItems].sort(byOrderOrDefault),
    [links, menues, deferredItems],
  );

  return {
    isLoading,
    navigationItems,
  };
};
